import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTour } from '@/contexts/TourContext';
import { TourCard } from './TourCard';
import { TourMenuSnapshot } from './TourMenuSnapshot';
import { createPortal } from 'react-dom';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function TourOverlay() {
  const { isActive, currentStep, steps, actionFired, stepRunId, transitioning, stopTour } = useTour();
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null);
  const [anchorFound, setAnchorFound] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [showSnapshot, setShowSnapshot] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const stepRunIdRef = useRef(stepRunId);

  const step = steps[currentStep];

  useEffect(() => {
    stepRunIdRef.current = stepRunId;
  }, [stepRunId]);

  const isMenuStep = step?.id === 'open_video_menu';
  const isPersonalizeStep = false;
  const needsSnapshot = isMenuStep;

  const activeSelector = actionFired && step?.postActionSelector
    ? step.postActionSelector
    : step?.selector;

  const activePlacement = actionFired && step?.postActionPlacement
    ? step.postActionPlacement
    : step?.placement;

  const findElement = useCallback(() => {
    if (!activeSelector || activePlacement === 'center') {
      setHighlightRect(null);
      setAnchorFound(activePlacement === 'center');
      return;
    }

    let selector = activeSelector;
    let el = document.querySelector(selector) as HTMLElement;

    if (!el && needsSnapshot && (actionFired || isPersonalizeStep)) {
      el = document.querySelector('[data-tour="video-actions-snapshot"]') as HTMLElement;
      if (isPersonalizeStep) {
        const highlightEl = document.querySelector('[data-tour="snapshot-highlight-item"]') as HTMLElement;
        if (highlightEl) el = highlightEl;
      }
    }

    if (el) {
      const rect = el.getBoundingClientRect();
      // Ignora elemento sem dimensões (layout ainda não aplicado)
      if (rect.width === 0 && rect.height === 0) {
        setAnchorFound(false);
        setHighlightRect(null);
        return;
      }
      const padding = 10;
      setHighlightRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });
      setAnchorFound(true);
    } else {
      setAnchorFound(false);
      setHighlightRect(null);
    }
  }, [activeSelector, activePlacement, isMenuStep, actionFired]);

  useEffect(() => {
    if (!isActive || !needsSnapshot) {
      setShowSnapshot(false);
      return;
    }

    if (isPersonalizeStep) {
      setShowSnapshot(true);
      return;
    }

    if (isMenuStep) {
      setShowSnapshot(true);
    }
  }, [isActive, needsSnapshot, isMenuStep, isPersonalizeStep, actionFired, stepRunId]);

  const handleSnapshotRect = useCallback((rect: { top: number; left: number; width: number; height: number } | null) => {
    if (rect && needsSnapshot) {
      setHighlightRect(rect);
      setAnchorFound(true);
    }
  }, [needsSnapshot]);

  useEffect(() => {
    if (showSnapshot && !needsSnapshot) {
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(findElement);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [showSnapshot, findElement, needsSnapshot]);

  useEffect(() => {
    if (!isActive) return;

    const capturedRunId = stepRunId;
    const isStale = () => stepRunIdRef.current !== capturedRunId;

    setAnchorFound(false);
    setHighlightRect(null);

    setShowPulse(true);
    const pulseTimeout = setTimeout(() => {
      if (!isStale()) setShowPulse(false);
    }, 800);

    if (activeSelector && activePlacement !== 'center') {
      const el = document.querySelector(activeSelector) as HTMLElement;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    const initialDelay = setTimeout(() => {
      if (!isStale()) findElement();
    }, needsSnapshot ? 0 : 350);

    let attempts = 0;
    const interval = setInterval(() => {
      if (isStale()) { clearInterval(interval); return; }
      attempts++;
      findElement();
      if (attempts >= 20) clearInterval(interval);
    }, 250);

    return () => {
      clearInterval(interval);
      clearTimeout(initialDelay);
      clearTimeout(pulseTimeout);
    };
  }, [isActive, currentStep, findElement, activeSelector, activePlacement, actionFired, stepRunId]);

  useEffect(() => {
    if (!isActive || !anchorFound) return;

    let timeoutId: number | null = null;
    const handler = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (debounceRef.current) cancelAnimationFrame(debounceRef.current);
        debounceRef.current = requestAnimationFrame(findElement);
      }, 120);
    };

    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);

    const observer = new MutationObserver(handler);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });

    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
      observer.disconnect();
      if (debounceRef.current) cancelAnimationFrame(debounceRef.current);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isActive, anchorFound, findElement]);

  useEffect(() => {
    return () => setShowSnapshot(false);
  }, [currentStep]);

  if (!isActive) return null;

  const showDarkOverlay = !actionFired || !step?.postActionSelector;

  const isUploadStep = step?.id === 'add_video';
  const isFolderStep = step?.id === 'create_folder';
  const blockModalInteraction = (isUploadStep || isFolderStep) && actionFired;

  const overlayContent = (
    <>
      {blockModalInteraction && (
        <>
          <style>{`
            [data-tour="modal-upload"],
            [data-tour="modal-folder-create"] [role="dialog"],
            [data-tour="modal-folder-create"] {
              pointer-events: none !important;
              user-select: none !important;
            }
          `}</style>
          <div
            className="fixed inset-0 pointer-events-auto"
            style={{ zIndex: 10002, background: 'transparent' }}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          />
        </>
      )}

      {/* Dark overlay sem cutout */}
      {showDarkOverlay && !highlightRect && (
        <div
          className="fixed inset-0 pointer-events-auto"
          style={{ background: 'rgba(0,0,0,0.55)', zIndex: 9990 }}
          onClick={stopTour}
        />
      )}

      {/* Dark overlay para snapshot steps */}
      {showDarkOverlay && highlightRect && needsSnapshot && (
        <div
          className="fixed inset-0 pointer-events-auto"
          style={{ background: 'rgba(0,0,0,0.55)', zIndex: 9990 }}
          onClick={stopTour}
        />
      )}

      {/* Spotlight cutout para steps não-snapshot */}
      {showDarkOverlay && highlightRect && !needsSnapshot && (
        <div
          className="fixed pointer-events-none"
          style={{
            zIndex: 9990,
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            borderRadius: 14,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
          }}
        />
      )}

      {/* Click blocker */}
      {showDarkOverlay && highlightRect && (
        <div
          className="fixed inset-0 pointer-events-auto"
          style={{ zIndex: needsSnapshot ? 10002 : 9990, background: 'transparent' }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!needsSnapshot) {
              const x = e.clientX;
              const y = e.clientY;
              // Clique dentro do spotlight — deixa passar
              if (
                x >= highlightRect.left &&
                x <= highlightRect.left + highlightRect.width &&
                y >= highlightRect.top &&
                y <= highlightRect.top + highlightRect.height
              ) {
                return;
              }
            }
            // Clique fora — para o tour
            stopTour();
          }}
        />
      )}

      {/* Dashed blue border para steps não-snapshot */}
      {highlightRect && !needsSnapshot && (
        <div
          className="fixed pointer-events-none"
          style={{
            zIndex: actionFired ? 10001 : 9993,
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            borderRadius: 14,
            border: '2px dashed #4B7BFF',
            transition: 'top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease',
            animation: showPulse ? 'tour-highlight-pulse 0.6s ease-out' : undefined,
          }}
        />
      )}

      {/* Menu Snapshot */}
      {needsSnapshot && (
        <TourMenuSnapshot
          cardSelector='[data-tour="video-card-primary"]'
          visible={showSnapshot}
          stepRunId={stepRunId}
          highlightItem={isPersonalizeStep ? 'Personalizar' : undefined}
          centerInContent={true}
          onRectReady={handleSnapshotRect}
        />
      )}

      {/* Tour Card */}
      {!transitioning && (
        <TourCard
          highlightRect={highlightRect}
          anchorFound={anchorFound || showSnapshot}
          overridePlacement={actionFired && step?.postActionPlacement ? step.postActionPlacement : undefined}
        />
      )}
    </>
  );

  return createPortal(overlayContent, document.body);
}
